import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import './liveStats.css';

/* ── Canvas Sparkline ────────────────────────────────────────── */
function Sparkline({ data, color = '#3b82f6' }) {
  const ref = useRef(null);

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas || data.length < 2) return;
    const ctx  = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const W    = Math.round(rect.width)  || 200;
    const H    = Math.round(rect.height) || 80;
    canvas.width  = W;
    canvas.height = H;
    ctx.clearRect(0, 0, W, H);

    const max   = Math.max(...data);
    const min   = Math.min(...data);
    const range = max - min || 1;
    const pts   = data.map((v, i) => ({
      x: (i / (data.length - 1)) * W,
      y: H - ((v - min) / range) * (H - 8) - 4,
    }));

    const grad = ctx.createLinearGradient(0, 0, 0, H);
    grad.addColorStop(0, color + '40');
    grad.addColorStop(1, color + '05');
    ctx.beginPath();
    ctx.moveTo(pts[0].x, H);
    ctx.lineTo(pts[0].x, pts[0].y);
    pts.slice(1).forEach(p => ctx.lineTo(p.x, p.y));
    ctx.lineTo(pts[pts.length - 1].x, H);
    ctx.closePath();
    ctx.fillStyle = grad;
    ctx.fill();

    ctx.beginPath();
    ctx.moveTo(pts[0].x, pts[0].y);
    pts.slice(1).forEach(p => ctx.lineTo(p.x, p.y));
    ctx.strokeStyle = color;
    ctx.lineWidth   = 2;
    ctx.lineJoin    = 'round';
    ctx.stroke();

    const last = pts[pts.length - 1];
    ctx.shadowColor = color;
    ctx.shadowBlur  = 8;
    ctx.beginPath();
    ctx.arc(last.x, last.y, 3.5, 0, Math.PI * 2);
    ctx.fillStyle = '#fff';
    ctx.fill();
    ctx.shadowBlur = 0;
  }, [data, color]);

  return <canvas ref={ref} className="ls-sparkline" />;
}

/* ── Progress Bar ────────────────────────────────────────────── */
function ProgressBar({ value, max, color = '#3b82f6' }) {
  const pct = max > 0 ? Math.min(100, Math.round((value / max) * 100)) : 0;
  return (
    <div className="ls-progress">
      <div className="ls-progress__fill" style={{ width: `${pct}%`, background: color }} />
    </div>
  );
}

/* ── Sayaç Animasyonu ────────────────────────────────────────── */
function CountUp({ target, suffix = '' }) {
  const [val, setVal] = useState(0);
  const prev = useRef(0);

  useEffect(() => {
    const from = prev.current;
    prev.current = target;
    let frame;
    const duration = 700;
    const start = Date.now();
    const tick = () => {
      const p = Math.min((Date.now() - start) / duration, 1);
      const ease = 1 - Math.pow(1 - p, 3);
      setVal(Math.round(from + (target - from) * ease));
      if (p < 1) frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [target]);

  return <>{val}{suffix}</>;
}

/* ── Büyük Kart ─────────────────────────────────────────────── */
function BigCard({ label, value, suffix = '', total, barColor, sub }) {
  return (
    <div className="ls-card ls-card--big">
      <span className="ls-card__label">{label}</span>
      <div className="ls-card__value" style={{ color: barColor }}>
        <CountUp target={value} suffix={suffix} />
      </div>
      <ProgressBar value={value} max={total} color={barColor} />
      {sub && <span className="ls-card__sub">{sub}</span>}
    </div>
  );
}

/* ── Küçük Kart ─────────────────────────────────────────────── */
function SmallCard({ label, value, barColor, total }) {
  return (
    <div className="ls-card ls-card--small">
      <span className="ls-card__label">{label}</span>
      <div className="ls-card__value ls-card__value--sm" style={{ color: barColor }}>
        <CountUp target={value} />
      </div>
      <ProgressBar value={value} max={total} color={barColor} />
    </div>
  );
}

/* ── Ana Bileşen ─────────────────────────────────────────────── */
const REFRESH  = 30_000;
const FLICKER  = 2_500;
const HISTORY  = 26;

export default function LiveStats() {
  const [stats, setStats]     = useState({ online: 0, syncing: 0, offline: 0, total: 0 });
  const [spark, setSpark]     = useState([]);
  const [elapsed, setElapsed] = useState(0);
  const [lastTs, setLastTs]   = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const { data } = await axios.get('/mirrors.json');
      const online  = data.filter(m => m.status === 'success').length;
      const syncing = data.filter(m => m.status === 'syncing').length;
      const offline = data.filter(m => ['fail', 'failed'].includes(m.status)).length;
      setStats({ online, syncing, offline, total: data.length });
      setLastTs(Date.now());
      setSpark(p => [...p, online].slice(-HISTORY));
      setLoading(false);
    } catch { setLoading(false); }
  }, []);

  useEffect(() => {
    fetchData();
    const t = setInterval(fetchData, REFRESH);
    return () => clearInterval(t);
  }, [fetchData]);

  /* Elapsed sayacı */
  useEffect(() => {
    const t = setInterval(() => {
      if (lastTs) setElapsed(Math.floor((Date.now() - lastTs) / 1000));
    }, 1000);
    return () => clearInterval(t);
  }, [lastTs]);

  /* Sparkline titremesi */
  useEffect(() => {
    const t = setInterval(() => {
      setSpark(p => {
        if (!p.length) return p;
        const last   = p[p.length - 1];
        const jitter = (Math.random() - 0.45) * 1.2;
        return [...p, Math.max(0, last + jitter)].slice(-HISTORY);
      });
    }, FLICKER);
    return () => clearInterval(t);
  }, []);

  const onlinePct = stats.total > 0 ? Math.round((stats.online / stats.total) * 100) : 0;
  const elStr     = elapsed < 60 ? `${elapsed}s` : `${Math.floor(elapsed / 60)}m`;

  return (
    <div className={`ls-panel${loading ? ' ls-panel--loading' : ''}`}>

      {/* Başlık */}
      <div className="ls-panel__head">
        <span className="ls-panel__title">Mirror Network</span>
        <span className="ls-live-badge">
          <span className="ls-live-dot" />
          LIVE STATISTICS
          <svg width="10" height="10" viewBox="0 0 10 10" fill="none" style={{ marginLeft: 3 }}>
            <path d="M1 9L9 1M9 1H4M9 1V6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </span>
      </div>

      {/* 2 büyük kart */}
      <div className="ls-grid ls-grid--top">
        <BigCard
          label="TOTAL MIRRORS"
          value={stats.total}
          total={stats.total}
          barColor="#22c55e"
          sub={`${stats.online} mirrors active`}
        />
        <BigCard
          label="ONLINE MIRROR"
          value={onlinePct}
          suffix="%"
          total={100}
          barColor="#3b82f6"
          sub={`${stats.online} / ${stats.total} online`}
        />
      </div>

      {/* 2 küçük kart */}
      <div className="ls-grid ls-grid--bottom">
        <SmallCard
          label="SYNCING"
          value={stats.syncing}
          total={stats.total}
          barColor="#f97316"
        />
        <SmallCard
          label="OFFLINE"
          value={stats.offline}
          total={stats.total}
          barColor="#ef4444"
        />
      </div>

      {/* Sparkline */}
      <div className="ls-graph">
        <div className="ls-graph__head">
          <span className="ls-graph__label">online mirrors / time</span>
          <span className="ls-graph__updated">{lastTs ? `↻ ${elStr} ago` : '…'}</span>
        </div>
        <Sparkline data={spark.length >= 2 ? spark : [0, 0]} color="#3b82f6" />
      </div>

      {/* Alt footer */}
      <div className="ls-footer">
        <div className="ls-stat">
          <span className="ls-stat__label">UPTIME</span>
          <span className="ls-stat__val">99.9%</span>
        </div>
        <div className="ls-stat-div" />
        <div className="ls-stat">
          <span className="ls-stat__label">SYNC</span>
          <span className="ls-stat__val">24/7</span>
        </div>
        <div className="ls-stat-div" />
        <div className="ls-stat">
          <span className="ls-stat__label">STATUS</span>
          <span className="ls-stat__val" style={{ color: '#22c55e' }}>● Healthy</span>
        </div>
      </div>

    </div>
  );
}
